import React, {useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Carousel} from "antd";
import {CarouselRef} from "antd/es/carousel";
import DOMPurify from "dompurify";
import {News} from "../../types/types";
import {
  formatDate,
  replaceStringSpaces,
  splitTextByDelimiter2,
} from "../../utils/utils";
import styles from "./NewsDetails.module.scss";
import Spinner from "../../components/_shared/loader/Spinner";
import {Helmet} from "react-helmet";

const META_DESCRIPTION_MAX = 200;

const stripHtmlForMeta = (html?: string | null): string => {
  if (!html) return "";
  const text = new DOMParser()
      .parseFromString(html, "text/html")
      .body.textContent || "";
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length <= META_DESCRIPTION_MAX) return collapsed;
  return collapsed.slice(0, META_DESCRIPTION_MAX - 1).trimEnd() + "…";
};

const NewsDetails = () => {
  const {i18n} = useTranslation();
  const {title} = useParams<{ title: string }>();
  const location = useLocation();
  const carouselRef = useRef<CarouselRef>(null);

  const [newsData, setNewsData] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);

  const normalizedTitle = useMemo(() => {
    return replaceStringSpaces((title ?? "").toLowerCase().trim());
  }, [title]);

  const stateData = location.state as News | undefined;

  useEffect(() => {
    if (stateData) {
      setNewsData(stateData);
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchAllPagesUntilMatch = async () => {
      let page = 0;
      const size = 10;
      let found = null;

      while (!found) {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/posts/published?page=${page}&size=${size}`
        );
        if (!response.ok) break;

        const json = await response.json();
        const content: News[] = json.content;

        found = content.find((post) => {
          const translatedTitle =
              i18n.language === "sq" ? post.alTitle : post.enTitle;
          return (
              replaceStringSpaces((translatedTitle ?? "").toLowerCase().trim()) ===
              normalizedTitle
          );
        });

        if (found) break;

        if (json.last) break;
        page++;
      }

      if (found && !isCancelled) {
        const byId = await fetch(
            `${process.env.REACT_APP_API_URL}/posts/${found.id}`
        );
        const fullPost = await byId.json();
        if (!isCancelled) setNewsData(fullPost);
      }

      if (!isCancelled) setLoading(false);
    };

    fetchAllPagesUntilMatch();

    return () => {
      isCancelled = true;
    };
  }, [stateData, normalizedTitle, i18n.language]);

  if (loading || !newsData) return <Spinner/>;

  const ogTitle = i18n.language === "sq" ? newsData.alTitle : newsData.enTitle;
  const ogDescRaw =
      i18n.language === "sq"
          ? newsData.alDescription
          : newsData.enDescription;
  const ogDescription = stripHtmlForMeta(ogDescRaw);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const ogUrl = `${origin}${location.pathname}`;
  const ogImage = newsData.mainImageUrl || `${origin}/og-default.png`;
  const ogLocale = i18n.language === "sq" ? "sq_AL" : "en_US";

  const description =
      i18n.language === "sq"
          ? newsData?.alDescription
          : newsData?.enDescription;
  const descriptionSections: any = splitTextByDelimiter2(description);

  return (
      <div className={styles.MainWrapper}>
        <Helmet>
          <title>{ogTitle}</title>
          <meta name="description" content={ogDescription}/>
          <meta property="og:site_name" content="Federata e Futbollit e Kosovës"/>
          <meta property="og:locale" content={ogLocale}/>
          <meta property="og:type" content="article"/>
          <meta property="og:url" content={ogUrl}/>
          <meta property="og:title" content={ogTitle}/>
          <meta property="og:description" content={ogDescription}/>
          <meta property="og:image" content={ogImage}/>
          <meta property="og:image:width" content="1200"/>
          <meta property="og:image:height" content="630"/>
          {newsData.publishedDate && (
              <meta property="article:published_time" content={newsData.publishedDate}/>
          )}
          <meta name="twitter:card" content="summary_large_image"/>
          <meta name="twitter:title" content={ogTitle}/>
          <meta name="twitter:description" content={ogDescription}/>
          <meta name="twitter:image" content={ogImage}/>
          <link rel="canonical" href={ogUrl}/>
        </Helmet>

        <div className={styles.ContentWrapper}>
          <h1>{ogTitle}</h1>
          <img src={newsData.mainImageUrl} alt="news main image"/>

          <div className={styles.DescriptionWrapper}>
            <p>
              {newsData.publishedDate &&
                  new Date(newsData.publishedDate).toLocaleString("sq-AL", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "Europe/Tirane",
                  })}
            </p>
            <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(descriptionSections, {
                    ALLOWED_TAGS: ["b", "i", "em", "u", "a", "p", "br", "ul", "ol", "li"],
                  }),
                }}
            />
          </div>

          {newsData.pdfUrl && (
              <div className={styles.IframeWrapper}>
                <iframe src={newsData.pdfUrl} title="pdf"/>
              </div>
          )}

          {newsData.videoUrls?.length > 0 && (
              <div className={styles.VideoWrapper}>
                {newsData.videoUrls.map((url, index) => {
                  const youtubeId = url.includes("youtube.com")
                      ? url.split("v=")[1]?.split("&")[0]
                      : null;
                  const facebookEmbed = url.includes("facebook.com");

                  return (
                      <div key={index} className={styles.VideoIframe}>
                        {youtubeId ? (
                            <iframe
                                src={`https://www.youtube.com/embed/${youtubeId}`}
                                title={`YouTube video ${index + 1}`}
                                allowFullScreen
                            />
                        ) : facebookEmbed ? (
                            <iframe
                                src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`}
                                title={`Facebook video ${index + 1}`}
                                allowFullScreen
                            />
                        ) : (
                            <p>Unsupported video format: {url}</p>
                        )}
                      </div>
                  );
                })}
              </div>
          )}

          {newsData.additionalImageUrls?.length > 0 && (
              <div className={styles.AdditionalImagesWrapper}>
                <Carousel
                    ref={carouselRef}
                    autoplay
                    dots={false}
                    infinite
                    className={styles.AdditionalImages}
                >
                  {newsData.additionalImageUrls.map((imageUrl, index) => (
                      <img
                          src={imageUrl}
                          alt={`additional image ${index + 1}`}
                          key={index}
                      />
                  ))}
                </Carousel>

                {newsData.additionalImageUrls.length > 1 && (
                    <div className={styles.ArrowWrapper}>
                      <button
                          className={styles.Arrow}
                          onClick={() => carouselRef.current?.prev()}
                      >
                        ‹
                      </button>
                      <button
                          className={styles.Arrow}
                          onClick={() => carouselRef.current?.next()}
                      >
                        ›
                      </button>
                    </div>
                )}
              </div>
          )}
        </div>
      </div>
  );
};

export default NewsDetails;